import {
	createSubscribeCheckout,
	createSubscribeCheckoutWithTrial,
} from 'data/reducers/subscriptionReducer';
import { FREE_TRIAL_CONFIG } from 'constants/freeTrialConfig';
import { SubscriptionHelper } from 'view/helpers';

/** Shared in-flight promise so SignIn/SignUp onSubmit + isLoggedIn effects do not double-fire. */
let resumeInFlight = null;

/** Test-only: clear the in-flight lock between cases. */
export function __resetResumeCheckoutForTests() {
	resumeInFlight = null;
}

/**
 * Creates a Stripe Checkout session from the plan stored before login/signup.
 * On success the subscribe thunk navigates to Stripe via window.location.
 *
 * @param {Function} dispatch - Redux dispatch
 * @returns {Promise<boolean>} true when a checkout URL was returned (redirect imminent)
 */
export function resumeCheckoutAfterAuth(dispatch) {
	if (resumeInFlight) {
		return resumeInFlight;
	}

	const planData = SubscriptionHelper.getPendingCheckout();
	if (!planData?.priceId) {
		return Promise.resolve(false);
	}

	resumeInFlight = (async () => {
		// Signup sets userData before sign-in finishes; wait for a bearer token.
		const authToken = dispatch((_, getState) => getState().user?.authToken);
		if (!authToken) {
			resumeInFlight = null;
			return false;
		}

		// Replay the add-on as the pricing modal stored it; the API prices SMS for the plan's interval.
		const withSmsAddon = !!planData.withSmsAddon;

		try {
			let response;
			if (planData.withTrial) {
				response = await dispatch(
					createSubscribeCheckoutWithTrial(
						planData.priceId,
						FREE_TRIAL_CONFIG.DEFAULT_DAYS,
						FREE_TRIAL_CONFIG.DEFAULT_SOURCE,
						withSmsAddon,
					),
				);
			} else {
				response = await dispatch(
					createSubscribeCheckout(planData.priceId, withSmsAddon),
				);
			}

			if (response?.checkoutUrl) {
				SubscriptionHelper.clearPendingCheckout();
				return true;
			}

			resumeInFlight = null;
			return false;
		} catch (error) {
			console.error('Failed to start Stripe checkout after auth:', error);
			resumeInFlight = null;
			return false;
		}
	})();

	return resumeInFlight;
}

/**
 * After auth: start Stripe when a plan was selected, otherwise use the normal redirect.
 *
 * @param {Object} options
 * @param {Function} options.dispatch
 * @param {Object} options.history - react-router history
 * @param {URLSearchParams} options.urlParams
 * @param {boolean} [options.invitationConsumed]
 * @returns {Promise<void>}
 */
export async function navigateAfterAuth({
	dispatch,
	history,
	urlParams,
	invitationConsumed = false,
}) {
	const invitationToken = invitationConsumed
		? null
		: urlParams?.get(SubscriptionHelper.INVITATION_TOKEN_PARAM);

	if (invitationToken) {
		history.replace(
			SubscriptionHelper.getPostAuthRedirect(urlParams, { invitationConsumed }),
		);
		return;
	}

	const startedCheckout = await resumeCheckoutAfterAuth(dispatch);
	if (startedCheckout) {
		return;
	}

	// Plan is stored but authToken is not ready yet (signup sets userData
	// before sign-in finishes). Stay put; the auth page retries once tokened.
	// A refused checkout falls through to pricing, which replays it and shows the error.
	const hasAuthToken = !!dispatch((_, getState) => getState().user?.authToken);
	if (SubscriptionHelper.getPendingCheckout()?.priceId && !hasAuthToken) {
		return;
	}

	history.push(
		SubscriptionHelper.getPostAuthRedirect(urlParams, { invitationConsumed }),
	);
}
